import React from 'react';
import { useSelector } from 'react-redux';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/system/colorManipulator';
import AlertIcon from './alert-icon';
import StatusIcon from './status-icon';
import TimeoutIcon from './timeout-icon';
import DateRangesIcon from './date-ranges-icon';
import {
  ListContainer,
  StyledList,
  StyledListItem,
  HeaderListItemIcon,
  StyledListItemIcon,
  HeaderListItemText,
  DeviceListItemText,
  StyledListSubheader,
  StyledListItemSecondaryAction,
  IconCnt,
  IconCntLeft,
  VerticalIconCnt
} from './style';

export default function SidebarList(props) {
  const {
    devices,
    selectedDevices,
    setDevices,
    rangeStart,
    rangeEnd,
    account,
    timeoutAlerts = [],
    devicesWithExcursions = []
  } = props;
  const checked = selectedDevices.map(d => d.uid);
  const tinted = devices.filter(d => d.tinted).map(d => d.uid);
  const { applianceDevices } = useSelector(s => s.applianceDevices);
  const uniqDevices = [...new Set(devices.map(d => d.uid))];
  const startDate = new Date(rangeStart).toISOString();
  const endDate = new Date(rangeEnd).toISOString();
  const deviceDateRange = `t/90/dr/${startDate}/${endDate}`;
  const sideBarSelectedDevices = devices.filter(d => !d.tinted);

  let devicesWithGroups = [];
  if (applianceDevices.length) {
    applianceDevices.forEach(g => {
      devicesWithGroups.push({ ...g, type: 'subheader' });
      g.devices.forEach(gd => {
        // devices could not be available during the initial renderings
        const dwg = devices.find(d => d.value === gd.id && d.projectAlertId === gd.projectAlertId);
        if (dwg) {
          devicesWithGroups.push({ ...dwg, dateRanges: gd.dateRanges });
        }
      });
    });
  } else {
    devicesWithGroups = devices;
  }

  const handleToggle = (value, selectOne) => () => {
    const currentIndex = checked.indexOf(value);
    let newChecked = [...checked];

    if (selectOne) {
      newChecked = [value];
    } else if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setDevices(devices.filter(d => newChecked.includes(d.uid)), selectOne);
  };

  const handleAll = () => {
    if (uniqDevices.length === checked.length && tinted.length === 0) {
      setDevices([]);
    } else {
      setDevices(devices, true);
    }
  };

  const stopPropagation = e => {
    e.stopPropagation();
  };

  return (
    <ListContainer elevation={0}>
      <StyledList>
        <StyledListItem
          key="all"
          button
          onClick={handleAll}
        >
          <HeaderListItemIcon />
          <HeaderListItemText
            tinted={uniqDevices.length !== checked.length}
            id="list-label-devices"
            primary={checked.length === uniqDevices.length ?
              `Devices (${uniqDevices.length})` : `Devices (${checked.length}/${uniqDevices.length})`}
          />
          <StyledListItemSecondaryAction>
            <StatusIcon
              onClick={handleAll}
              checked={checked.length !== 0}
              indeterminate={uniqDevices.length !== checked.length && checked.length !== 0}
              title="Show or hide all devices"
            />
          </StyledListItemSecondaryAction>
        </StyledListItem>
        {devicesWithGroups.map((d, i) => {
          const labelId = `checkbox-list-secondary-label-${d.uid}`;
          const iconProps = {
            fontSize: 'small',
            color: 'action'
          };
          const showAxisInfo = sideBarSelectedDevices.some(sd => sd.uid === d.uid);
          if (d.type === 'subheader') {
            return (
              <StyledListSubheader key={d.id + i}>
                {d.label}
              </StyledListSubheader>
            );
          }
          return (
            <StyledListItem
              key={`${d.uid}${i}`}
              role={undefined}
              dense
              button
              onClick={handleToggle(d.uid, true)}
            >
              <StyledListItemIcon>
                <StopRoundedIcon
                  style={{ color: checked.indexOf(d.uid) === -1 || tinted.indexOf(d.uid) !== -1 ? alpha(`#${d.color}`, 0.25) : `#${d.color}` }}
                  fontSize="large"
                />
              </StyledListItemIcon>
              <DeviceListItemText
                id={labelId}
                primary={d.uid}
                secondary={showAxisInfo && d.selectedAxis}
                tinted={checked.indexOf(d.uid) === -1}
              />
              <IconCnt>
                <IconCntLeft>
                  <AlertIcon
                    account={account}
                    device={d}
                    iconProps={iconProps}
                    stopPropagation={stopPropagation}
                    devicesWithExcursions={devicesWithExcursions}
                  />
                  <VerticalIconCnt>
                    <DateRangesIcon
                      account={account}
                      dateRanges={d.dateRanges}
                      iconProps={iconProps}
                      queryRangeStart={startDate}
                      queryRangeEnd={endDate}
                    />
                    <TimeoutIcon
                      account={account}
                      timeoutAlert={timeoutAlerts.find(a => a.id === d.value)}
                      iconProps={iconProps}
                    />
                  </VerticalIconCnt>
                </IconCntLeft>
                <Tooltip title="Open the device">
                  <Link
                    href={`${window.location.origin}/device/${d.value}/${deviceDateRange}`}
                    color="inherit"
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <IconButton onClick={stopPropagation} size="small">
                      <LaunchIcon {...iconProps} />
                    </IconButton>
                  </Link>
                </Tooltip>
              </IconCnt>
              <StyledListItemSecondaryAction>
                <StatusIcon
                  onClick={handleToggle(d.uid)}
                  checked={checked.indexOf(d.uid) !== -1}
                  indeterminate={tinted.indexOf(d.uid) !== -1 && checked.indexOf(d.uid) !== -1}
                  title="Show or hide device"
                />
              </StyledListItemSecondaryAction>
            </StyledListItem>
          );
        })}
      </StyledList>
    </ListContainer>
  );
}
